import { applyFilterMutation } from 'gqlQueries/filterQueries';
import { getApplyFilterObject } from 'parser/filterParser';
import { client } from 'services/apolloInit';
import { deleteTypeNameFromResponse } from 'utils/helperMethods';
import type { SimulatorAppliedFilters, SimulatorFilterCategory } from './SimulatorFilterPanel';
import { isTrustedPrecomputedCategoryApplyFilter } from './simulatorFilterExcludeInclude.helpers';
import {
    DEFAULT_SIMULATOR_TABLE_DIMENSIONS,
    SIMULATOR_FILTER_FEATURE_NAME,
    toSimulatorFilterAttributeType,
    type GlobalFilterInclusionItem,
    type SimulatorFilterScopeItem,
} from './simulatorFilter.helpers';

type ApplyFilterAttributePayload = {
    inclusion?: GlobalFilterInclusionItem[];
    exclusion?: GlobalFilterInclusionItem[];
};

type ApplyFilterDimensionPayload = Record<string, ApplyFilterAttributePayload>;

type ApplyFilterGraphqlResponse = Record<string, { ids?: Array<number | string> } | undefined>;

function collectSimulatorFilterDimensions(filterCategories: SimulatorFilterCategory[]): string[] {
    const dimensions = new Set<string>(DEFAULT_SIMULATOR_TABLE_DIMENSIONS);
    filterCategories.forEach((category) => {
        if (category.dimension) {
            dimensions.add(category.dimension);
        }
    });
    return [...dimensions];
}

function buildEmptyTableFilters(dimensions: string[]): Record<string, number[]> {
    return Object.fromEntries(dimensions.map((dimension) => [dimension, []]));
}

function pushApplyFilterAttributeIds(
    result: Record<string, ApplyFilterDimensionPayload>,
    dimension: string,
    attributeType: string,
    mode: 'inclusion' | 'exclusion',
    ids: string[],
) {
    if (!ids.length) return;

    if (!result[dimension]) {
        result[dimension] = {};
    }
    if (!result[dimension][attributeType]) {
        result[dimension][attributeType] = {};
    }

    const bucket = result[dimension][attributeType][mode] ?? [];
    for (const attributeExId of ids) {
        bucket.push({
            attributeType,
            attributeDesc: attributeExId,
            attributeExId,
            dimension,
        });
    }
    result[dimension][attributeType][mode] = bucket;
}

function applyCategorySelectionToFilterObject(
    result: Record<string, ApplyFilterDimensionPayload>,
    category: SimulatorFilterCategory,
    appliedFilters: Pick<
        SimulatorAppliedFilters,
        'categorySelections' | 'categorySelectAll' | 'categoryApplyFilters' | 'categoryUsedSelectAll'
    >,
) {
    const {
        categorySelections,
        categorySelectAll = {},
        categoryApplyFilters = {},
        categoryUsedSelectAll = {},
    } = appliedFilters;
    const selectedIds = categorySelections[category.key] ?? [];
    const isSelectAll = Boolean(categorySelectAll[category.key]);
    const usedSelectAll = Boolean(categoryUsedSelectAll[category.key]);
    const dimension = category.dimension ?? 'dim1';
    const attributeType = category.attributeType ?? toSimulatorFilterAttributeType(category.key);
    const precomputed = categoryApplyFilters[category.key];

    if (isSelectAll && selectedIds.length === 0) {
        if (!result[dimension]) {
            result[dimension] = {};
        }
        result[dimension][attributeType] = {};
        return;
    }

    if (
        isTrustedPrecomputedCategoryApplyFilter(precomputed, selectedIds, {
            isSelectAll,
            usedSelectAll,
        })
    ) {
        if (precomputed?.inclusion !== undefined) {
            pushApplyFilterAttributeIds(result, dimension, attributeType, 'inclusion', precomputed.inclusion);
            return;
        }

        if (precomputed?.exclusion?.length) {
            pushApplyFilterAttributeIds(result, dimension, attributeType, 'exclusion', precomputed.exclusion);
            return;
        }
    }

    if (selectedIds.length > 0) {
        pushApplyFilterAttributeIds(result, dimension, attributeType, 'inclusion', selectedIds);
    }
}

/** Stages scenario scope + filter-panel selections into global-filter applyFilter shape. */
function buildSimulatorApplyFilterObject(
    filterCategories: SimulatorFilterCategory[],
    appliedFilters: Pick<
        SimulatorAppliedFilters,
        'categorySelections' | 'categorySelectAll' | 'categoryApplyFilters' | 'categoryUsedSelectAll'
    >,
    appliedScope: SimulatorFilterScopeItem[] = [],
): Record<string, ApplyFilterDimensionPayload> {
    const result: Record<string, ApplyFilterDimensionPayload> = {};

    for (const item of appliedScope) {
        if (!item.attribute_type) continue;
        const dimension = item.dimension ?? 'dim1';
        for (const exId of item.attribute_ex_id ?? []) {
            pushApplyFilterAttributeIds(result, dimension, item.attribute_type, 'inclusion', [String(exId)]);
        }
    }

    for (const category of filterCategories) {
        applyCategorySelectionToFilterObject(result, category, appliedFilters);
    }

    return result;
}

function hasApplyFilterPayload(applyFilterObject: Record<string, ApplyFilterDimensionPayload>): boolean {
    return Object.values(applyFilterObject).some((dimensionPayload) =>
        Object.values(dimensionPayload).some(
            (attributePayload) =>
                (attributePayload.inclusion?.length ?? 0) > 0 ||
                (attributePayload.exclusion?.length ?? 0) > 0 ||
                Object.keys(attributePayload).length === 0,
        ),
    );
}

function parseApplyFilterResponseToTableFilters(
    response: ApplyFilterGraphqlResponse,
    dimensions: string[],
): Record<string, number[]> {
    const filters = buildEmptyTableFilters(dimensions);

    for (const dimension of dimensions) {
        const ids = response[dimension]?.ids ?? [];
        filters[dimension] = ids
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));
    }

    return filters;
}

/** Resolves scenario scope + filter-panel selections to numeric dim ids via applyFilter GraphQL. */
export async function resolveSimulatorTableFiltersViaApplyFilter({
    filterCategories,
    appliedFilters = { categorySelections: {} },
    appliedScope = [],
}: {
    filterCategories: SimulatorFilterCategory[];
    appliedFilters?: Pick<
        SimulatorAppliedFilters,
        'categorySelections' | 'categorySelectAll' | 'categoryApplyFilters' | 'categoryUsedSelectAll'
    >;
    appliedScope?: SimulatorFilterScopeItem[];
}): Promise<Record<string, number[]>> {
    const dimensions = collectSimulatorFilterDimensions(filterCategories);
    const applyFilterObject = buildSimulatorApplyFilterObject(
        filterCategories,
        appliedFilters,
        appliedScope,
    );

    if (!hasApplyFilterPayload(applyFilterObject)) {
        return buildEmptyTableFilters(dimensions);
    }

    const mutationVariables = getApplyFilterObject(applyFilterObject);
    const variables: Record<string, string | undefined> = {
        featureName: SIMULATOR_FILTER_FEATURE_NAME,
    };

    for (const [key, value] of Object.entries(mutationVariables)) {
        variables[key] = JSON.stringify(value);
    }

    const { data } = await client.mutate({
        mutation: applyFilterMutation,
        variables,
    });

    const response = (data ?? {}) as ApplyFilterGraphqlResponse;
    deleteTypeNameFromResponse(response);

    return parseApplyFilterResponseToTableFilters(response, dimensions);
}
